import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

export const ProfessionalCollections = () => {
  const navigate = useNavigate();
  const { id, name } = useParams();
  const [collections, setCollections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;

    const fetchCategories = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `https://webadmin.naturx.co.in/api/get_category?main_cat_id=${id}`,
        );
        if (!response.ok) {
          throw new Error("Failed to fetch collection data.");
        }
        const data = await response.json();
        setCollections(data.data || []);
      } catch (err) {
        console.error(err.message);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, [id]);

  return (
    <div className="min-h-screen bg-slate-50 lg:py-10 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <header className="mb-8 text-left">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-orange-500/10 text-orange-600 text-xs font-semibold uppercase tracking-wider mb-3">
            <span>Explore Range</span>
          </div>

          <h1 className="text-xl lg:text-2xl font-semibold text-slate-900 tracking-tight leading-tight">
            Professional{" "}
            <span className="relative inline-block text-orange-600">
              Products
              <span className="absolute left-0 bottom-1 w-full h-1.5 bg-orange-500/30 -z-10 rounded-full"></span>
            </span>{" "}
            <span className="text-slate-400 font-light mx-1">/</span>{" "}
            <span className="text-slate-600 font-medium capitalize">
              {name || "Collection"}
            </span>
          </h1>
        </header>

        {/* Loading Skeleton Grid */}
        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
              <div
                key={item}
                className="w-full rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden animate-pulse"
              >
                <div className="aspect-[4/3] w-full bg-slate-200"></div>
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-slate-200 rounded-md w-3/4"></div>
                  <div className="h-3 bg-slate-200 rounded-md w-full"></div>
                  <div className="h-3 bg-slate-200 rounded-md w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="text-center py-12 px-4 rounded-3xl bg-red-50 border border-red-100 max-w-md mx-auto">
            <p className="text-red-600 font-medium text-lg mb-2">
              Failed to load content
            </p>
            <p className="text-red-400 text-sm mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2.5 bg-red-600 text-white font-medium text-sm rounded-xl hover:bg-red-700 transition-colors shadow-sm"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Cards Grid */}
        {!isLoading && !error && (
          <>
            {collections.length === 0 ? (
              <div className="text-center py-24 bg-white rounded-3xl border border-slate-100 shadow-sm">
                <p className="text-slate-500 text-lg">
                  No collections found for this category.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
                {collections.map((collection) => (
                  <div
                    key={collection.id}
                    onClick={() =>
                      navigate(`/collections/${collection.id}/${id}`)
                    }
                    className="group flex flex-col bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden cursor-pointer"
                  >
                    {/* Top Section: Image Container */}
                    <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100">
                      <img
                        src={collection.image}
                        alt={collection.name || "Collection"}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>

                    {/* Bottom Section: Title & Description */}
                    <div className="p-5 flex flex-col flex-grow justify-between bg-white">
                      <div>
                        <h3 className="text-slate-900 text-md font-semibold tracking-tight mb-2 line-clamp-1 group-hover:text-orange-600 transition-colors">
                          {collection.name}
                        </h3>

                        {collection.description && (
                          <p className="text-slate-600 text-sm leading-relaxed line-clamp-2">
                            {collection.description}
                          </p>
                        )}
                      </div>

                      {/* Card Footer / Action Link */}
                      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-sm font-semibold text-orange-600">
                        <span>Explore Category</span>
                        <svg
                          className="w-4 h-4 transform group-hover:translate-x-1 transition-transform"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
